'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { ControlBarMoreAction } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActions';
import ControlBarActionTooltip from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarActionTooltip';
import { CONTROL_BAR_ACTION_CLASS } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarDesktopLayout';
import ControlBarEmbedLogoButton from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarEmbedLogoButton';
import type { ControlBarViewportLayoutProps } from '@/app/[locale]/(worlds)/[roomId]/components/ControlBarViewportLayout';
import FullscreenButton from '@/app/[locale]/(worlds)/[roomId]/components/FullscreenButton';
import SharePopoverButton from '@/app/[locale]/(worlds)/[roomId]/components/SharePopoverButton';
import type { DesktopControlBarVariant } from '@/constants/control-bar';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useIsFullscreenAvailable } from '@/hooks/useIsFullscreenAvailable';
import { cn } from '@/lib/utils';
import type { RecommendRoom } from '@/services/cms';
type EmbeddedControlBarProps = {
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
  popupContainer?: HTMLDivElement | null;
  variant?: DesktopControlBarVariant;
};
type EmbeddedControlBarViewportOptions = {
  moreRecommendationsPromise?: Promise<RecommendRoom[]> | null;
  variant?: DesktopControlBarVariant;
};

/**
 * Content tiers follow the iframe width through container queries on the bar (the bar is as wide
 * as the iframe): 769px+ has the `Play on VIVERSE` logo button, More, Share, and Fullscreen,
 * 481px–768px drops `Play on`, 320px–480px keeps the logo and Share, 250px–319px keeps the logo,
 * and below 250px only Fullscreen remains. Like, Save, and Report are not offered: they need a
 * login, and the login page cannot be shown inside an iframe.
 */
export function EmbeddedControlBar(t0) {
  const $ = _c(41);
  const {
    moreRecommendationsPromise,
    popupContainer,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const t = useTranslations();
  const {
    roomData
  } = useCheckEnterRoomContext();
  const isProVariant = variant === "viverse-pro";
  const isFullscreenAvailable = useIsFullscreenAvailable();
  const contentKind = roomData.content_type?.code === "games" ? "games" : "experiences";
  const t2 = !isFullscreenAvailable && (isProVariant ? "@max-embed-sm/embed:hidden" : "@max-embed-xs/embed:hidden");
  let t3;
  if ($[0] !== t2) {
    t3 = cn("bg-control-bar-surface relative w-full rounded-b-lg text-white", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isProVariant || $[3] !== popupContainer) {
    t4 = !isProVariant && <div className="@min-embed-xs/embed:contents hidden"><ControlBarEmbedLogoButton popupContainer={popupContainer} variant="responsive" /></div>;
    $[2] = isProVariant;
    $[3] = popupContainer;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="flex min-w-0 items-center">{t4}</div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== contentKind || $[8] !== isProVariant || $[9] !== moreRecommendationsPromise || $[10] !== popupContainer) {
    t6 = !isProVariant && <div className="@min-embed-md/embed:contents hidden"><ControlBarMoreAction contentKind={contentKind} moreRecommendationsPromise={moreRecommendationsPromise} popupContainer={popupContainer} /></div>;
    $[7] = contentKind;
    $[8] = isProVariant;
    $[9] = moreRecommendationsPromise;
    $[10] = popupContainer;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("action-list.share");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== popupContainer) {
    t8 = <SharePopoverButton className={CONTROL_BAR_ACTION_CLASS} iconClassName="size-4 @[954px]:size-5" portalContainer={popupContainer} size="custom" useControlBarDialog={true} />;
    $[14] = popupContainer;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== popupContainer || $[17] !== t7 || $[18] !== t8) {
    t9 = <div className="@min-embed-sm/embed:contents hidden"><ControlBarActionTooltip label={t7} portalContainer={popupContainer}>{t8}</ControlBarActionTooltip></div>;
    $[16] = popupContainer;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("control-bar.fullscreen");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("control-bar.fullscreen");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <span className="hidden text-sm leading-[22px] font-semibold @[954px]:inline">{t11}</span>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12) {
    t13 = <FullscreenButton ariaLabel={t10} className={CONTROL_BAR_ACTION_CLASS} iconClassName="size-4 @[954px]:size-5" label={t12} />;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t9) {
    t14 = <div className="ml-2 flex items-center gap-1 rounded-3xl backdrop-blur-xl">{t9}{t13}</div>;
    $[29] = t13;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t6) {
    t15 = <div className="flex shrink-0 items-center justify-end">{t6}{t14}</div>;
    $[32] = t14;
    $[33] = t6;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15 || $[36] !== t5) {
    t16 = <div className="@container flex min-h-12 w-full flex-nowrap items-center justify-between gap-2 p-2">{t5}{t15}</div>;
    $[35] = t15;
    $[36] = t5;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16 || $[39] !== t3) {
    t17 = <div data-component="embedded-control-bar" className={t3}>{t16}</div>;
    $[38] = t16;
    $[39] = t3;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}
export function createEmbeddedControlBarViewportLayout({
  moreRecommendationsPromise,
  variant = 'default'
}: EmbeddedControlBarViewportOptions): Omit<ControlBarViewportLayoutProps, 'children'> {
  return {
    barPlacement: 'after',
    className: 'flex h-full min-h-0 min-w-0 flex-1 flex-col @container/embed',
    dataComponent: 'embedded-control-bar-layout',
    renderControlBar: popupContainer => <EmbeddedControlBar moreRecommendationsPromise={moreRecommendationsPromise} popupContainer={popupContainer} variant={variant} />,
    viewportDataComponent: 'embedded-control-bar-viewport'
  };
}